import type { Language } from "./language";

export const APP_PROGRESS_UPDATED_EVENT = "moneytor:app-progress-updated";
const APP_PROGRESS_AUTO_OPEN_VERSION = 1;
const APP_PROGRESS_AUTO_OPEN_PREFIX = `moneytor:app-progress:auto-open:v${APP_PROGRESS_AUTO_OPEN_VERSION}`;

export type AppProgressOriginRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type AppProgressUpdateEventDetail = {
  stepKey?: AppProgressStepKey;
  originRect?: AppProgressOriginRect;
  eventId: number;
};

type AppProgressUpdateOptions = {
  origin?: HTMLElement | EventTarget | null;
  originRect?: AppProgressOriginRect;
  userId?: string | null;
};

type AppProgressClientSkipReason =
  | "disabled"
  | "missing_user"
  | "already_completed"
  | "in_flight";

export type AppProgressClientMarkResult =
  | { ok: true; skipped: false; response: Response }
  | { ok: false; skipped: false; response: Response }
  | { ok: true; skipped: true; reason: AppProgressClientSkipReason };

const appProgressInFlightSteps = new Set<string>();
let isAppProgressClientEnabled = false;

export function configureAppProgressClientAccess(isEnabled: boolean) {
  isAppProgressClientEnabled = isEnabled;
}

function getAppProgressAutoOpenKey(userId?: string | null) {
  return `${APP_PROGRESS_AUTO_OPEN_PREFIX}:${userId ?? "anonymous"}`;
}

function getAppProgressInFlightKey(
  userId: string,
  stepKey: AppProgressStepKey
) {
  return `${userId}:${stepKey}`;
}

export function shouldOpenAppProgressByDefault(userId?: string | null) {
  if (typeof window === "undefined") return false;
  try {
    return (
      window.localStorage.getItem(getAppProgressAutoOpenKey(userId)) !== "false"
    );
  } catch {
    return false;
  }
}

export function writeAppProgressDefaultOpenPreference(
  userId: string | null | undefined,
  shouldOpen: boolean
) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      getAppProgressAutoOpenKey(userId),
      shouldOpen ? "true" : "false"
    );
  } catch {
    // localStorage can be unavailable in private mode or full storage.
  }
}

function getOriginRect(
  origin?: HTMLElement | EventTarget | null
): AppProgressOriginRect | undefined {
  if (!origin || typeof window === "undefined") return undefined;
  if (!(origin instanceof HTMLElement)) return undefined;

  const rect = origin.getBoundingClientRect();
  return {
    x: rect.left,
    y: rect.top,
    width: rect.width,
    height: rect.height,
  };
}

export function notifyAppProgressUpdated(
  detail: Partial<Omit<AppProgressUpdateEventDetail, "eventId">> = {}
) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<AppProgressUpdateEventDetail>(APP_PROGRESS_UPDATED_EVENT, {
      detail: {
        ...detail,
        eventId: Date.now(),
      },
    })
  );
}

export async function markAppProgressStepClient(
  stepKey: AppProgressStepKey,
  options: AppProgressUpdateOptions = {}
): Promise<AppProgressClientMarkResult> {
  if (!isAppProgressClientEnabled) {
    return { ok: true, skipped: true, reason: "disabled" };
  }

  if (!options.userId) {
    return { ok: true, skipped: true, reason: "missing_user" };
  }

  if (
    readCachedAppProgress(options.userId).some(
      (completion) => completion.key === stepKey
    )
  ) {
    return { ok: true, skipped: true, reason: "already_completed" };
  }

  const inFlightKey = getAppProgressInFlightKey(options.userId, stepKey);
  if (appProgressInFlightSteps.has(inFlightKey)) {
    return { ok: true, skipped: true, reason: "in_flight" };
  }

  appProgressInFlightSteps.add(inFlightKey);
  const originRect = options.originRect ?? getOriginRect(options.origin);

  const formData = new FormData();
  formData.set("step_key", stepKey);

  try {
    const response = await fetch("/app-progress", {
      method: "POST",
      body: formData,
    });
    if (!response.ok) {
      notifyAppProgressUpdated();
      return { ok: false, skipped: false, response };
    }

    writeCachedAppProgressStep(options.userId, stepKey);
    notifyAppProgressUpdated({
      stepKey,
      originRect,
    });

    return { ok: true, skipped: false, response };
  } finally {
    appProgressInFlightSteps.delete(inFlightKey);
  }
}

export const APP_PROGRESS_STEP_KEYS = {
  MANUAL_ASSET_CREATED: "manual_asset_created",
  PREMIUM_TRIED: "premium_tried",
  BANK_CONNECTED: "bank_connected",
  CREDIT_CARD_CONNECTED: "credit_card_connected",
  PENSION_IMPORTED: "pension_imported",
  CASHFLOW_VISITED: "cashflow_visited",
  CASHFLOW_RULE_CREATED: "cashflow_rule_created",
  CASHFLOW_MANUAL_CATEGORY_CHANGED: "cashflow_manual_category_changed",
  BUDGET_VISITED: "budget_visited",
  BUDGET_AUTO_ASSIGNED: "budget_auto_assigned",
  BUDGET_CATEGORY_MANUALLY_CHANGED: "budget_category_manually_changed",
  ASSETS_VISITED: "assets_visited",
  LIABILITIES_VISITED: "liabilities_visited",
  INSIGHT_OPENED: "insight_opened",
  DASHBOARD_ANALYZE_CLICKED: "dashboard_analyze_clicked",
  ASSETS_MAP_CATEGORY_CLICKED: "assets_map_category_clicked",
  OPPORTUNITIES_OPENED: "opportunities_opened",
} as const;

export type AppProgressStepKey =
  (typeof APP_PROGRESS_STEP_KEYS)[keyof typeof APP_PROGRESS_STEP_KEYS];

export type AppProgressGroupKey =
  | "setup"
  | "cashflow"
  | "budget"
  | "assets"
  | "dashboard"
  | "premium";

export type AppProgressStepDefinition = {
  key: AppProgressStepKey;
  group: AppProgressGroupKey;
  href?: string;
  focusId?: string;
  innerFocusId?: string;
  opensPremiumModal?: boolean;
  lockedForFree?: boolean;
  dependsOn?: AppProgressStepKey[];
};

export type AppProgressStepState = AppProgressStepDefinition & {
  title: string;
  description: string;
  prerequisiteLockedLabel: string;
  completed: boolean;
  storedCompleted: boolean;
  derivedCompleted: boolean;
  completedAt: string | null;
  premiumLocked: boolean;
  prerequisiteLocked: boolean;
  locked: boolean;
};

export type AppProgressCompletion = {
  key: AppProgressStepKey;
  completed: boolean;
  storedCompleted: boolean;
  derivedCompleted: boolean;
  completedAt: string | null;
};

export type AppProgressLoaderData = {
  progress: AppProgressCompletion[];
};

export type CachedAppProgressCompletion = {
  key: AppProgressStepKey;
  completedAt: string;
};

export type AppProgressGroupStatus = "empty" | "partial" | "complete";

export type AppProgressGroupStats = {
  group: AppProgressGroupKey;
  completedCount: number;
  totalCount: number;
  percentage: number;
  status: AppProgressGroupStatus;
};

export type AppProgressState = {
  steps: AppProgressStepState[];
  completedCount: number;
  totalCount: number;
  percentage: number;
  groupStats: AppProgressGroupStats[];
  groupsCompletedCount: number;
  groupsTotalCount: number;
};

export function buildAppProgressState(
  completions: AppProgressCompletion[],
  isPremiumUser: boolean,
  lang: Language,
  options: { includePremiumOpportunities?: boolean } = {}
): AppProgressState {
  const completionByKey = new Map(completions.map((item) => [item.key, item]));
  const includePremiumOpportunities =
    options.includePremiumOpportunities ?? true;
  const translations =
    appProgressTranslations[lang] ?? appProgressTranslations.HEB;
  const visibleDefinitions = APP_PROGRESS_STEPS.filter(
    (definition) =>
      includePremiumOpportunities || definition.group !== "premium"
  );
  const visibleDefinitionByKey = new Map(
    visibleDefinitions.map((definition) => [definition.key, definition])
  );
  const completedByKey = new Map<AppProgressStepKey, boolean>();

  visibleDefinitions.forEach((definition) => {
    completedByKey.set(
      definition.key,
      Boolean(completionByKey.get(definition.key)?.completed)
    );
  });

  const steps = visibleDefinitions.map((definition) => {
    const completion = completionByKey.get(definition.key);
    const copy = translations.steps[definition.key];
    const premiumLocked = Boolean(definition.lockedForFree && !isPremiumUser);
    const missingDependencyKeys =
      definition.dependsOn?.filter((dependencyKey) => {
        return (
          visibleDefinitionByKey.has(dependencyKey) &&
          completedByKey.get(dependencyKey) !== true
        );
      }) ?? [];
    const prerequisiteLocked = missingDependencyKeys.length > 0;
    const prerequisiteLockedLabel = missingDependencyKeys.includes(
      APP_PROGRESS_STEP_KEYS.BANK_CONNECTED
    )
      ? translations.bankConnectionRequiredLabel
      : translations.prerequisiteLockedLabel;
    const completed = !prerequisiteLocked && Boolean(completion?.completed);

    return {
      ...definition,
      title: copy.title,
      description: copy.description,
      prerequisiteLockedLabel,
      completed,
      storedCompleted: Boolean(completion?.storedCompleted),
      derivedCompleted: Boolean(completion?.derivedCompleted),
      completedAt: completion?.completedAt ?? null,
      premiumLocked,
      prerequisiteLocked,
      locked: premiumLocked || prerequisiteLocked,
    };
  });
  const completedCount = steps.filter((step) => step.completed).length;
  const totalCount = steps.length;
  const groupStats = APP_PROGRESS_GROUP_ORDER.map((group) => {
    const groupSteps = steps.filter((step) => step.group === group);
    const groupCompletedCount = groupSteps.filter(
      (step) => step.completed
    ).length;
    const groupTotalCount = groupSteps.length;
    const status: AppProgressGroupStatus =
      groupCompletedCount === groupTotalCount && groupTotalCount > 0
        ? "complete"
        : groupCompletedCount > 0
        ? "partial"
        : "empty";

    return {
      group,
      completedCount: groupCompletedCount,
      totalCount: groupTotalCount,
      percentage:
        groupTotalCount > 0
          ? Math.round((groupCompletedCount / groupTotalCount) * 100)
          : 0,
      status,
    };
  }).filter((group) => group.totalCount > 0);
  const groupsCompletedCount = groupStats.filter(
    (group) => group.status === "complete"
  ).length;

  return {
    steps,
    completedCount,
    totalCount,
    percentage:
      totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0,
    groupStats,
    groupsCompletedCount,
    groupsTotalCount: groupStats.length,
  };
}

const APP_PROGRESS_CACHE_VERSION = 1;
const APP_PROGRESS_CACHE_PREFIX = `moneytor:app-progress:v${APP_PROGRESS_CACHE_VERSION}`;

type AppProgressCachePayload = {
  version: number;
  completions: CachedAppProgressCompletion[];
};

function getAppProgressCacheKey(userId: string) {
  return `${APP_PROGRESS_CACHE_PREFIX}:${userId}`;
}

function normalizeCachedCompletions(
  value: unknown
): CachedAppProgressCompletion[] {
  if (!value || typeof value !== "object") return [];
  const completions = (value as Partial<AppProgressCachePayload>).completions;
  if (!Array.isArray(completions)) return [];

  return completions.filter(
    (completion): completion is CachedAppProgressCompletion => {
      return (
        Boolean(completion) &&
        isAppProgressStepKey(completion.key) &&
        typeof completion.completedAt === "string"
      );
    }
  );
}

export function isAppProgressStepKey(
  value: string
): value is AppProgressStepKey {
  return APP_PROGRESS_STEPS.some((step) => step.key === value);
}

export function readCachedAppProgress(
  userId?: string | null
): CachedAppProgressCompletion[] {
  if (!userId || typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(getAppProgressCacheKey(userId));
    if (!raw) return [];

    return normalizeCachedCompletions(JSON.parse(raw));
  } catch {
    return [];
  }
}

export function writeCachedAppProgress(
  userId: string | null | undefined,
  completions: CachedAppProgressCompletion[]
) {
  if (!userId || typeof window === "undefined") return [];

  const uniqueCompletions = new Map<
    AppProgressStepKey,
    CachedAppProgressCompletion
  >();
  completions.forEach((completion) => {
    if (!isAppProgressStepKey(completion.key)) return;
    uniqueCompletions.set(completion.key, completion);
  });

  const payload: AppProgressCachePayload = {
    version: APP_PROGRESS_CACHE_VERSION,
    completions: Array.from(uniqueCompletions.values()),
  };

  try {
    window.localStorage.setItem(
      getAppProgressCacheKey(userId),
      JSON.stringify(payload)
    );
  } catch {
    // localStorage can be unavailable in private mode or full storage.
  }

  return payload.completions;
}

export function writeCachedAppProgressStep(
  userId: string | null | undefined,
  stepKey: AppProgressStepKey,
  completedAt = new Date().toISOString()
) {
  if (!userId) return [];

  const completions = readCachedAppProgress(userId);
  const existingIndex = completions.findIndex(
    (completion) => completion.key === stepKey
  );
  if (existingIndex >= 0) {
    completions[existingIndex] = {
      ...completions[existingIndex],
      completedAt: completions[existingIndex].completedAt || completedAt,
    };
  } else {
    completions.push({ key: stepKey, completedAt });
  }

  return writeCachedAppProgress(userId, completions);
}

export function hydrateCachedAppProgressFromServer(
  userId: string | null | undefined,
  serverProgress: AppProgressCompletion[]
) {
  if (!userId) return [];

  const cached = readCachedAppProgress(userId);
  const cachedByKey = new Map(
    cached.map((completion) => [completion.key, completion])
  );

  serverProgress.forEach((completion) => {
    if (!completion.completed || !isAppProgressStepKey(completion.key)) return;
    cachedByKey.set(completion.key, {
      key: completion.key,
      completedAt:
        completion.completedAt ??
        cachedByKey.get(completion.key)?.completedAt ??
        new Date().toISOString(),
    });
  });

  return writeCachedAppProgress(userId, Array.from(cachedByKey.values()));
}

export function mergeAppProgressCompletions(
  serverProgress: AppProgressCompletion[],
  cachedProgress: CachedAppProgressCompletion[]
): AppProgressCompletion[] {
  const completionsByKey = new Map<AppProgressStepKey, AppProgressCompletion>();

  serverProgress.forEach((completion) => {
    completionsByKey.set(completion.key, completion);
  });

  cachedProgress.forEach((cached) => {
    const serverCompletion = completionsByKey.get(cached.key);
    if (serverCompletion?.completed) return;

    completionsByKey.set(cached.key, {
      key: cached.key,
      completed: true,
      storedCompleted: true,
      derivedCompleted: Boolean(serverCompletion?.derivedCompleted),
      completedAt: serverCompletion?.completedAt ?? cached.completedAt,
    });
  });

  return APP_PROGRESS_STEPS.map((step) => {
    return (
      completionsByKey.get(step.key) ?? {
        key: step.key,
        completed: false,
        storedCompleted: false,
        derivedCompleted: false,
        completedAt: null,
      }
    );
  });
}

export const APP_PROGRESS_STEPS: AppProgressStepDefinition[] = [
  {
    key: APP_PROGRESS_STEP_KEYS.MANUAL_ASSET_CREATED,
    group: "setup",
    href: "/assets",
    focusId: "add-product-btn",
  },
  {
    key: APP_PROGRESS_STEP_KEYS.PREMIUM_TRIED,
    group: "premium",
    opensPremiumModal: true,
    focusId: "premium-continue-button",
  },
  {
    key: APP_PROGRESS_STEP_KEYS.BANK_CONNECTED,
    group: "setup",
    href: "/openFinance/connect",
    lockedForFree: true,
  },
  {
    key: APP_PROGRESS_STEP_KEYS.CREDIT_CARD_CONNECTED,
    group: "setup",
    href: "/openFinance/connect?restrictedTo=CARD",
    lockedForFree: true,
    dependsOn: [APP_PROGRESS_STEP_KEYS.BANK_CONNECTED],
  },
  {
    key: APP_PROGRESS_STEP_KEYS.PENSION_IMPORTED,
    group: "setup",
    href: "/assets?masleka_open=true",
    lockedForFree: true,
  },
  {
    key: APP_PROGRESS_STEP_KEYS.CASHFLOW_VISITED,
    group: "cashflow",
    href: "/cashflow",
    focusId: "nav-cashflow",
    lockedForFree: true,
    dependsOn: [APP_PROGRESS_STEP_KEYS.BANK_CONNECTED],
  },
  {
    key: APP_PROGRESS_STEP_KEYS.CASHFLOW_RULE_CREATED,
    group: "cashflow",
    href: "/cashflow",
    focusId: "cashflow-transaction-actions-button",
    innerFocusId: "cashflow-create-rule-action",
    lockedForFree: true,
    dependsOn: [
      APP_PROGRESS_STEP_KEYS.BANK_CONNECTED,
      APP_PROGRESS_STEP_KEYS.CASHFLOW_VISITED,
    ],
  },
  {
    key: APP_PROGRESS_STEP_KEYS.BUDGET_VISITED,
    group: "budget",
    href: "/cashflow/budget",
    focusId: "budget-auto-assign-button",
    lockedForFree: true,
    dependsOn: [APP_PROGRESS_STEP_KEYS.BANK_CONNECTED],
  },
  {
    key: APP_PROGRESS_STEP_KEYS.BUDGET_AUTO_ASSIGNED,
    group: "budget",
    href: "/cashflow/budget",
    focusId: "budget-manage-menu-trigger",
    innerFocusId: "budget-auto-assign-menu-item",
    lockedForFree: true,
    dependsOn: [
      APP_PROGRESS_STEP_KEYS.BANK_CONNECTED,
      APP_PROGRESS_STEP_KEYS.BUDGET_VISITED,
    ],
  },
  {
    key: APP_PROGRESS_STEP_KEYS.BUDGET_CATEGORY_MANUALLY_CHANGED,
    group: "budget",
    href: "/cashflow/budget?tab=expenses",
    focusId: "budget-first-category-row",
    lockedForFree: true,
    dependsOn: [
      APP_PROGRESS_STEP_KEYS.BANK_CONNECTED,
      APP_PROGRESS_STEP_KEYS.BUDGET_AUTO_ASSIGNED,
    ],
  },
  {
    key: APP_PROGRESS_STEP_KEYS.ASSETS_VISITED,
    group: "assets",
    href: "/assets",
    focusId: "nav-assets",
  },
  {
    key: APP_PROGRESS_STEP_KEYS.LIABILITIES_VISITED,
    group: "assets",
    href: "/assets",
    focusId: "assets-liabilities-tab",
    dependsOn: [APP_PROGRESS_STEP_KEYS.ASSETS_VISITED],
  },
  {
    key: APP_PROGRESS_STEP_KEYS.INSIGHT_OPENED,
    group: "dashboard",
    href: "/insights",
    focusId: "first-insight-card",
  },
  {
    key: APP_PROGRESS_STEP_KEYS.DASHBOARD_ANALYZE_CLICKED,
    group: "dashboard",
    href: "/",
    focusId: "historic-analysis-btn",
    lockedForFree: true,
  },
  {
    key: APP_PROGRESS_STEP_KEYS.ASSETS_MAP_CATEGORY_CLICKED,
    group: "dashboard",
    href: "/",
    focusId: "assets-sunburst-first-category",
  },
  {
    key: APP_PROGRESS_STEP_KEYS.OPPORTUNITIES_OPENED,
    group: "premium",
    href: "/opportunities",
    focusId: "nav-opportunities",
  },
];

export const APP_PROGRESS_GROUP_ORDER: AppProgressGroupKey[] = [
  "setup",
  "assets",
  "dashboard",
  "cashflow",
  "budget",
  "premium",
];

type TranslationRecord = Record<
  Language,
  {
    title: string;
    subtitle: string;
    collapsedLabel: string;
    actionLabel: string;
    returnLabel: string;
    doneLabel: string;
    lockedLabel: string;
    prerequisiteLockedLabel: string;
    bankConnectionRequiredLabel: string;
    partsCompletedLabel: string;
    closeLabel: string;
    openLabel: string;
    premiumLockTitle: string;
    minimizeLabel: string;
    showAllLabel: string;
    completionTitle: string;
    completionDescription: string;
    groups: Record<AppProgressGroupKey, string>;
    steps: Record<AppProgressStepKey, { title: string; description: string }>;
  }
>;

export const appProgressTranslations: TranslationRecord = {
  HEB: {
    title: "ההתקדמות שלך במאניטור",
    subtitle: "השלימו פעולות מרכזיות כדי להפיק יותר מהמערכת.",
    collapsedLabel: "התקדמות",
    actionLabel: "ביצוע",
    returnLabel: "חזרה",
    doneLabel: "בוצע",
    lockedLabel: "נעול לפרימיום",
    prerequisiteLockedLabel: "השלימו קודם את השלב הקודם",
    bankConnectionRequiredLabel:
      "יש לסיים את חיבור הבנק בהקמה וחיבורים.",
    partsCompletedLabel: "חלקים שהושלמו",
    closeLabel: "סגור",
    openLabel: "פתח התקדמות",
    premiumLockTitle: "השלב הזה פתוח למשתמשי פרימיום",
    minimizeLabel: "מזערו את משימת ההתקדמות",
    showAllLabel: "הציגו את כל משימות ההתקדמות",
    completionTitle: "כל הכבוד השלמת את השלבים בהצלחה",
    completionDescription: "אפשר לחזור למשימות ההתקדמות בכל זמן מהתפריט.",
    groups: {
      setup: "הקמה וחיבורים",
      cashflow: "תזרים",
      budget: "תקציב",
      assets: "נכסים",
      dashboard: "דשבורד ותובנות",
      premium: "פרימיום והזדמנויות",
    },
    steps: {
      manual_asset_created: {
        title: "צרו נכס ידני",
        description: "הוסיפו נכס דרך כפתור הפלוס במסך הנכסים.",
      },
      premium_tried: {
        title: "נסו את מנוי הפרימיום",
        description: "פתחו את מסך הפרימיום ובחרו את המסלול שמתאים לכם.",
      },
      bank_connected: {
        title: "חברו בנק",
        description: "חברו חשבון בנק דרך Open Finance.",
      },
      credit_card_connected: {
        title: "חברו כרטיס אשראי",
        description: "חברו כרטיס כדי לראות עסקאות ותזרים מלאים.",
      },
      pension_imported: {
        title: "ייבאו נכסים פנסיוניים",
        description: "ייבאו קובץ מסלקה או התחברו למסלקה אוטומטית.",
      },
      cashflow_visited: {
        title: "בקרו בעמוד התזרים",
        description: "פתחו את עמוד התזרים כדי לראות הכנסות והוצאות.",
      },
      cashflow_rule_created: {
        title: "צרו כלל שינוי קטגוריה",
        description: "הגדירו כלל שמסווג עסקאות באופן אוטומטי.",
      },
      cashflow_manual_category_changed: {
        title: "שנו קטגוריה לעסקה",
        description: "בחרו עסקה ועדכנו לה קטגוריה מתאימה.",
      },
      budget_auto_assigned: {
        title: "צרו תקציב עם שיוך אוטומטי",
        description: "הפעילו שיוך אוטומטי לקטגוריות התקציב.",
      },
      budget_visited: {
        title: "עברו לעמוד התקציב",
        description: "פתחו את תפריט ניהול התזרים ולחצו על מעבר לתקציב.",
      },
      budget_category_manually_changed: {
        title: "שנו תקציב לקטגוריה",
        description: "עדכנו ידנית תקציב לקטגוריה ספציפית.",
      },
      assets_visited: {
        title: "בקרו בעמוד הנכסים",
        description: "פתחו את עמוד הנכסים והתחייבויות.",
      },
      liabilities_visited: {
        title: "עברו ללשונית ההתחייבויות",
        description: "פתחו את לשונית ההתחייבויות בעמוד הנכסים.",
      },
      insight_opened: {
        title: "פתחו תובנה",
        description: "לחצו על תובנה וקראו את ההמלצות.",
      },
      dashboard_analyze_clicked: {
        title: "לחצו על ניתוח בדשבורד",
        description: "פתחו את ניתוח גרף השווי ההיסטורי.",
      },
      assets_map_category_clicked: {
        title: "לחצו על קטגוריה במפת הנכסים",
        description: "העמיקו לתוך קטגוריה בגרף הנכסים.",
      },
      opportunities_opened: {
        title: "פתחו את עמוד ההזדמנויות",
        description: "גלו הצעות והזדמנויות פיננסיות.",
      },
    },
  },
  ENG: {
    title: "Your Moneytor progress",
    subtitle: "Complete key actions to get more value from the app.",
    collapsedLabel: "Progress",
    actionLabel: "Go",
    returnLabel: "Return",
    doneLabel: "Done",
    lockedLabel: "Premium locked",
    prerequisiteLockedLabel: "Complete the previous step first",
    bankConnectionRequiredLabel:
      "Complete the bank connection in setup and connections.",
    partsCompletedLabel: "parts completed",
    closeLabel: "Close",
    openLabel: "Open progress",
    premiumLockTitle: "This step is available for premium users",
    minimizeLabel: "Minimize progress item",
    showAllLabel: "Show all progress tasks",
    completionTitle: "Great job, you completed all steps successfully",
    completionDescription: "You can reopen progress tasks from the sidebar anytime.",
    groups: {
      setup: "Setup and connections",
      cashflow: "Cashflow",
      budget: "Budget",
      assets: "Assets",
      dashboard: "Dashboard and insights",
      premium: "Premium and opportunities",
    },
    steps: {
      manual_asset_created: {
        title: "Create a manual asset",
        description: "Add an asset from the plus button on the assets page.",
      },
      premium_tried: {
        title: "Try the premium subscription",
        description: "Open the premium screen and choose the right plan.",
      },
      bank_connected: {
        title: "Connect a bank",
        description: "Connect a bank account with Open Finance.",
      },
      credit_card_connected: {
        title: "Connect a credit card",
        description: "Connect a card to see full cashflow activity.",
      },
      pension_imported: {
        title: "Import pension assets",
        description: "Import a Masleka file or connect automatic Masleka sync.",
      },
      cashflow_visited: {
        title: "Visit the cashflow page",
        description: "Open cashflow to review income and expenses.",
      },
      cashflow_rule_created: {
        title: "Create a category rule",
        description:
          "Create a rule that categorizes transactions automatically.",
      },
      cashflow_manual_category_changed: {
        title: "Change a transaction category",
        description: "Select a transaction and update its category.",
      },
      budget_auto_assigned: {
        title: "Create a budget with auto assign",
        description: "Use automatic assignment for budget categories.",
      },
      budget_visited: {
        title: "Go to the budget page",
        description:
          "Open the cashflow management menu and click go to budget.",
      },
      budget_category_manually_changed: {
        title: "Manually change a category budget",
        description: "Update a budget for a specific category.",
      },
      assets_visited: {
        title: "Visit the assets page",
        description: "Open the assets and liabilities page.",
      },
      liabilities_visited: {
        title: "Go to the liabilities tab",
        description: "Open the liabilities tab on the assets page.",
      },
      insight_opened: {
        title: "Open an insight",
        description: "Open an insight and read the recommendation.",
      },
      dashboard_analyze_clicked: {
        title: "Click dashboard analysis",
        description: "Open the historic worth analysis panel.",
      },
      assets_map_category_clicked: {
        title: "Click an assets map category",
        description: "Drill into a category in the assets map.",
      },
      opportunities_opened: {
        title: "Open the opportunities page",
        description: "Browse financial opportunities and offers.",
      },
    },
  },
  RUS: {
    title: "Ваш прогресс в Moneytor",
    subtitle: "Выполните ключевые действия, чтобы получить больше пользы.",
    collapsedLabel: "Прогресс",
    actionLabel: "Выполнить",
    returnLabel: "Назад",
    doneLabel: "Готово",
    lockedLabel: "Только Premium",
    prerequisiteLockedLabel: "Сначала выполните предыдущий шаг",
    bankConnectionRequiredLabel:
      "Завершите подключение банка в настройке и подключениях.",
    partsCompletedLabel: "разделов выполнено",
    closeLabel: "Закрыть",
    openLabel: "Открыть прогресс",
    premiumLockTitle: "Этот шаг доступен пользователям Premium",
    minimizeLabel: "Свернуть задачу прогресса",
    showAllLabel: "Показать все задачи прогресса",
    completionTitle: "Отлично, вы успешно завершили все шаги",
    completionDescription:
      "Вы можете снова открыть прогресс из бокового меню в любое время.",
    groups: {
      setup: "Настройка и подключения",
      cashflow: "Денежный поток",
      budget: "Бюджет",
      assets: "Активы",
      dashboard: "Дашборд и инсайты",
      premium: "Premium и возможности",
    },
    steps: {
      manual_asset_created: {
        title: "Создайте актив вручную",
        description: "Добавьте актив через кнопку плюс на странице активов.",
      },
      premium_tried: {
        title: "Попробуйте Premium",
        description: "Откройте экран Premium и выберите подходящий план.",
      },
      bank_connected: {
        title: "Подключите банк",
        description: "Подключите банковский счет через Open Finance.",
      },
      credit_card_connected: {
        title: "Подключите кредитную карту",
        description: "Подключите карту, чтобы видеть полный денежный поток.",
      },
      pension_imported: {
        title: "Импортируйте пенсионные активы",
        description: "Импортируйте файл Masleka или подключите синхронизацию.",
      },
      cashflow_visited: {
        title: "Откройте страницу cashflow",
        description: "Посмотрите доходы и расходы.",
      },
      cashflow_rule_created: {
        title: "Создайте правило категории",
        description: "Создайте правило для автоматической категоризации.",
      },
      cashflow_manual_category_changed: {
        title: "Измените категорию транзакции",
        description: "Выберите транзакцию и обновите ее категорию.",
      },
      budget_auto_assigned: {
        title: "Создайте бюджет с автоназначением",
        description: "Используйте автоматическое назначение категорий бюджета.",
      },
      budget_visited: {
        title: "Перейдите на страницу бюджета",
        description:
          "Откройте меню управления денежным потоком и перейдите в бюджет.",
      },
      budget_category_manually_changed: {
        title: "Измените бюджет категории",
        description: "Обновите бюджет для конкретной категории.",
      },
      assets_visited: {
        title: "Откройте страницу активов",
        description: "Посмотрите страницу активов и обязательств.",
      },
      liabilities_visited: {
        title: "Перейдите на вкладку обязательств",
        description: "Откройте вкладку обязательств на странице активов.",
      },
      insight_opened: {
        title: "Откройте инсайт",
        description: "Откройте инсайт и прочитайте рекомендацию.",
      },
      dashboard_analyze_clicked: {
        title: "Нажмите анализ на дашборде",
        description: "Откройте анализ исторической стоимости.",
      },
      assets_map_category_clicked: {
        title: "Нажмите категорию на карте активов",
        description: "Углубитесь в категорию на карте активов.",
      },
      opportunities_opened: {
        title: "Откройте страницу возможностей",
        description: "Посмотрите финансовые возможности и предложения.",
      },
    },
  },
};
